import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { taskService } from '../services/taskService';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import StatCard from '../components/dashboard/StatCard';
import ChartCard from '../components/dashboard/ChartCard';
import TableSkeleton from '../components/common/TableSkeleton';
import Loading from '../components/common/Loading';
import InlineToast from '../components/common/InlineToast';

const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#0ea5e9', '#ef4444', '#8b5cf6', '#14b8a6'];

const getDefaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10)
  };
};

const getLastWeekStarts = (endDateString, count = 4) => {
  const end = new Date(`${endDateString}T00:00:00.000Z`);
  const day = end.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  end.setUTCDate(end.getUTCDate() + diff);
  end.setUTCHours(0, 0, 0, 0);

  const weeks = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i * 7);
    weeks.push(d.toISOString().slice(0, 10));
  }
  return weeks;
};

const getUtilizationStyle = (hours) => {
  if (hours > 90) {
    return { bg: '#fecaca', text: '#991b1b', label: '> 90h (Overloaded)' };
  }
  if (hours > 75) {
    return { bg: '#fde68a', text: '#92400e', label: '76-90h (High)' };
  }
  if (hours >= 40) {
    return { bg: '#86efac', text: '#14532d', label: '40-75h (Healthy)' };
  }
  return { bg: '#dcfce7', text: '#166534', label: '< 40h (Low)' };
};

const HeatmapGrid = ({ rows = [], rowKey = 'personName', endDate }) => {
  const xBuckets = getLastWeekStarts(endDate, 4);
  const yLabels = Array.from(new Set(rows.map((row) => row[rowKey]))).sort();

  if (yLabels.length === 0) {
    return <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">No heatmap data in this range.</div>;
  }

  const cellMap = new Map(rows.map((row) => [`${row[rowKey]}__${row.bucket}`, row.hours]));

  return (
    <div className="overflow-x-auto">
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1 rounded border border-green-200 px-2 py-1 text-green-800">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#dcfce7' }} aria-hidden="true" />
          &lt; 40h
        </span>
        <span className="inline-flex items-center gap-1 rounded border border-green-300 px-2 py-1 text-green-900">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#86efac' }} aria-hidden="true" />
          40-75h
        </span>
        <span className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-amber-900">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#fde68a' }} aria-hidden="true" />
          76-90h
        </span>
        <span className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-red-900">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#fecaca' }} aria-hidden="true" />
          &gt; 90h
        </span>
      </div>
      <table className="min-w-full text-xs">
        <thead>
          <tr>
            <th className="text-left py-2 pr-3 text-gray-500">{rowKey === 'managerName' ? 'Manager' : 'Person'}</th>
            {xBuckets.map((bucket) => (
              <th key={bucket} className="text-center py-2 px-2 text-gray-500">{bucket}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {yLabels.map((label) => (
            <tr key={label}>
              <td className="py-1 pr-3 text-gray-700 whitespace-nowrap">{label}</td>
              {xBuckets.map((bucket) => {
                const value = Number(cellMap.get(`${label}__${bucket}`) || 0);
                const style = getUtilizationStyle(value);
                return (
                  <td key={`${label}-${bucket}`} className="px-2 py-1">
                    <div
                      className="h-7 rounded text-center leading-7 text-[11px]"
                      style={{
                        backgroundColor: style.bg,
                        color: style.text
                      }}
                      title={`${label} - ${bucket}: ${value.toFixed(2)}h (${style.label})`}
                      aria-label={`${label} ${bucket} ${value.toFixed(1)} hours ${style.label}`}
                    >
                      {value > 0 ? value.toFixed(1) : '-'}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TeamDashboard = () => {
  const { hasRole } = useAuth();
  const canView = hasRole('Manager') || hasRole('Admin') || hasRole('Executive');
  const isCompanyViewer = hasRole('Admin') || hasRole('Executive');
  const canControlAssignmentState = hasRole('Manager') || hasRole('Admin');

  const [filters, setFilters] = useState({
    ...getDefaultRange(),
    scope: isCompanyViewer ? 'company' : 'directReports',
    clientId: '',
    priority: '',
    status: '',
    managerId: '',
    departmentId: '',
    userId: ''
  });
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [selectedBlockerTheme, setSelectedBlockerTheme] = useState('');
  const [assignmentUpdatingTaskId, setAssignmentUpdatingTaskId] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardService.getTeamDashboard({
        start: filters.start,
        end: filters.end,
        scope: filters.scope,
        clientId: filters.clientId || undefined,
        priority: filters.priority || undefined,
        status: filters.status || undefined,
        managerId: filters.managerId || undefined,
        departmentId: filters.departmentId || undefined,
        userId: filters.userId || undefined
      });
      setDashboard(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load team dashboard.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, loadDashboard]);

  const stackedCustomerData = useMemo(() => {
    const rows = dashboard?.series?.hoursByCustomerStacked || [];
    const people = Array.from(new Set(rows.map((row) => row.personName))).slice(0, 6);
    const customerMap = new Map();

    rows.forEach((row) => {
      if (!people.includes(row.personName)) return;
      const existing = customerMap.get(row.customer) || { customer: row.customer };
      existing[row.personName] = row.hours;
      customerMap.set(row.customer, existing);
    });

    return {
      data: Array.from(customerMap.values()).slice(0, 12),
      people
    };
  }, [dashboard]);

  const blockersTaskRows = useMemo(() => {
    const rows = dashboard?.series?.blockerTasks || [];
    if (!selectedBlockerTheme) return rows;
    return rows.filter((row) => row.theme === selectedBlockerTheme);
  }, [dashboard, selectedBlockerTheme]);

  const employeeTaskStateSummary = dashboard?.series?.employeeTaskStateSummary || [];
  const employeeAssignmentTasks = useMemo(() => {
    const rows = dashboard?.tables?.employeeAssignmentTasks || [];
    if (!filters.userId) return rows;
    return rows.filter((row) => String(row.assigneeId) === String(filters.userId));
  }, [dashboard, filters.userId]);

  const handleAssignmentStateChange = async (taskId, assignmentState) => {
    try {
      setAssignmentUpdatingTaskId(taskId);
      await taskService.updateTask(taskId, { assignmentState });
      setToast({ message: `Task marked as ${assignmentState}.`, type: 'success' });
      await loadDashboard();
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update assignment state.',
        type: 'error'
      });
    } finally {
      setAssignmentUpdatingTaskId(null);
    }
  };

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  const summary = dashboard?.summary || {};
  const options = dashboard?.filterOptions || { clients: [], members: [], managers: [], departments: [] };
  const clientLabelToId = new Map(options.clients.map((option) => [option.label, String(option.value)]));

  const filtersNode = (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-start">Start Date</label>
        <input
          id="team-start"
          aria-label="Team dashboard start date"
          type="date"
          value={filters.start}
          onChange={(event) => setFilters((prev) => ({ ...prev, start: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-end">End Date</label>
        <input
          id="team-end"
          aria-label="Team dashboard end date"
          type="date"
          value={filters.end}
          onChange={(event) => setFilters((prev) => ({ ...prev, end: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-client">Client</label>
        <select
          id="team-client"
          aria-label="Filter by client"
          value={filters.clientId}
          onChange={(event) => setFilters((prev) => ({ ...prev, clientId: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">All Clients</option>
          {options.clients.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-priority">Priority</label>
        <select
          id="team-priority"
          aria-label="Filter by priority"
          value={filters.priority}
          onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">All Priority</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
          <option value="P4">P4</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-status">Status</label>
        <select
          id="team-status"
          aria-label="Filter by status"
          value={filters.status}
          onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">All Statuses</option>
          <option value="Planning">Planning</option>
          <option value="In Progress">In Progress</option>
          <option value="In Review">In Review</option>
          <option value="Blocked">Blocked</option>
          <option value="On Hold">On Hold</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>
      {isCompanyViewer && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-scope">Scope</label>
          <select
            id="team-scope"
            aria-label="Scope"
            value={filters.scope}
            onChange={(event) => setFilters((prev) => ({ ...prev, scope: event.target.value }))}
            className="w-full border rounded px-3 py-2"
          >
            <option value="company">Company Scope</option>
            <option value="directReports">My Direct Reports</option>
          </select>
        </div>
      )}
      {isCompanyViewer && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-manager">Manager</label>
          <select
            id="team-manager"
            aria-label="Manager filter"
            value={filters.managerId}
            onChange={(event) => setFilters((prev) => ({ ...prev, managerId: event.target.value }))}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Managers</option>
            {options.managers.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      )}
      {isCompanyViewer && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-department">Department</label>
          <select
            id="team-department"
            aria-label="Department filter"
            value={filters.departmentId}
            onChange={(event) => setFilters((prev) => ({ ...prev, departmentId: event.target.value }))}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Departments</option>
            {options.departments.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-member">Team Member</label>
        <select
          id="team-member"
          aria-label="Person filter"
          value={filters.userId}
          onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">All Members</option>
          {options.members.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="Team Dashboard" description="Team utilization, delivery risk, and execution trends." filters={filtersNode}>
        <Loading />
        <TableSkeleton rows={8} columns={6} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Team Dashboard" description="Team utilization, delivery risk, and execution trends." filters={filtersNode}>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{error}</div>}
      {toast.message && (
        <div className="mb-2">
          <InlineToast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'info' })} />
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Total Team Hours" value={summary.totalTeamHours || 0} />
        <StatCard title="Due Date Changes" value={summary.dueDateChangesCount || 0} />
        <StatCard title="Completed Projects" value={summary.completedProjectsCount || 0} />
        <StatCard title="Total Active Projects" value={summary.totalActiveProjects || 0} />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Employee Assignment Summary" subtitle="Per employee task counts">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="text-left py-2 pr-3">Employee</th>
                  <th className="text-right py-2 pr-3">Total</th>
                  <th className="text-right py-2 pr-3">Active</th>
                  <th className="text-right py-2 pr-3">Inactive</th>
                  <th className="text-right py-2 pr-3">Hours</th>
                  <th className="text-right py-2">Completed</th>
                </tr>
              </thead>
              <tbody>
                {employeeTaskStateSummary.map((row) => (
                  <tr
                    key={row.userId}
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setFilters((prev) => ({ ...prev, userId: String(row.userId) }))}
                  >
                    <td className="py-2 pr-3 text-primary hover:underline">{row.personName}</td>
                    <td className="py-2 pr-3 text-right">{row.totalTasks}</td>
                    <td className="py-2 pr-3 text-right">{row.activeTasks}</td>
                    <td className="py-2 pr-3 text-right">{row.inactiveTasks}</td>
                    <td className="py-2 pr-3 text-right">{Number(row.hours || 0).toFixed(2)}</td>
                    <td className="py-2 text-right">{row.completedTasks || 0}</td>
                  </tr>
                ))}
                {employeeTaskStateSummary.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 text-gray-500">No employee task data in this range.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Assignment State Controls" subtitle="Manager/Admin can mark each task active or inactive">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="text-left py-2 pr-3">Task</th>
                  <th className="text-left py-2 pr-3">Employee</th>
                  <th className="text-left py-2 pr-3">Current</th>
                  <th className="text-left py-2">Change State</th>
                </tr>
              </thead>
              <tbody>
                {employeeAssignmentTasks.slice(0, 20).map((row) => (
                  <tr key={row.taskId} className="border-b last:border-0">
                    <td className="py-2 pr-3 max-w-sm whitespace-normal break-words">
                      <Link className="text-primary hover:underline" to={`/tasks/${row.taskId}`}>
                        {row.title}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{row.assigneeName}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        row.assignmentState === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {row.assignmentState === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-2">
                      {canControlAssignmentState ? (
                        <select
                          className="border rounded px-2 py-1 text-xs"
                          value={row.assignmentState || 'inactive'}
                          disabled={assignmentUpdatingTaskId === row.taskId}
                          onChange={(event) => handleAssignmentStateChange(row.taskId, event.target.value)}
                        >
                          <option value="inactive">Inactive</option>
                          <option value="active">Active</option>
                        </select>
                      ) : (
                        <span className="text-xs text-gray-500">Manager controlled</span>
                      )}
                    </td>
                  </tr>
                ))}
                {employeeAssignmentTasks.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-3 text-gray-500">No tasks available for assignment controls.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Resource Utilization Heatmap" subtitle="Last 4 weeks by person">
          <HeatmapGrid rows={dashboard?.series?.hoursByPersonHeatmap || []} rowKey="personName" endDate={filters.end} />
        </ChartCard>

        <ChartCard title="Top Contributors" subtitle={dashboard?.meta?.caution}>
          {(dashboard?.series?.leaderboard || []).length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2 pr-3">Person</th>
                    <th className="text-right py-2 pr-3">Hours</th>
                    <th className="text-right py-2 pr-3">Completions</th>
                    <th className="text-right py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.series.leaderboard.slice(0, 10).map((row) => (
                    <tr key={row.userId} className="border-b last:border-0">
                      <td className="py-2 pr-3">{row.personName}</td>
                      <td className="py-2 pr-3 text-right">{row.hours.toFixed(2)}</td>
                      <td className="py-2 pr-3 text-right">{row.completions}</td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => setFilters((prev) => ({ ...prev, userId: String(row.userId) }))}
                        >
                          Filter
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">No contributors in this range.</div>
          )}
        </ChartCard>

        
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        

        <ChartCard
          title="Blocker Tasks"
          subtitle={selectedBlockerTheme ? `Filtered by theme: ${selectedBlockerTheme}` : 'Click a blocker theme to filter'}
          action={selectedBlockerTheme ? (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setSelectedBlockerTheme('')}
            >
              Clear Theme
            </button>
          ) : null}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="text-left py-2 pr-3">Task</th>
                  <th className="text-left py-2 pr-3">Theme</th>
                  <th className="text-left py-2 pr-3">Owner</th>
                  <th className="text-left py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {blockersTaskRows.slice(0, 8).map((row) => (
                  <tr key={`${row.theme}-${row.taskId}`} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <Link className="text-primary hover:underline" to={`/tasks/${row.taskId}`}>{row.title}</Link>
                    </td>
                    <td className="py-2 pr-3">{row.theme}</td>
                    <td className="py-2 pr-3">{row.owner}</td>
                    <td className="py-2">{row.date}</td>
                  </tr>
                ))}
                {blockersTaskRows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-3 text-gray-500">No blocker tasks for this filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Stalled Projects" subtitle="XS/S/M projects exceeding age threshold">
          {(dashboard?.tables?.stalledProjects || []).length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2 pr-3">Project</th>
                    <th className="text-right py-2 pr-3">Age (days)</th>
                    <th className="text-left py-2 pr-3">Owner</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.tables.stalledProjects.slice(0, 10).map((row) => (
                    <tr key={row.taskId} className="border-b last:border-0">
                      <td className="py-2 pr-3 max-w-xs whitespace-normal break-words">{row.title}</td>
                      <td className="py-2 pr-3 text-right">{row.ageDays}</td>
                      <td className="py-2 pr-3">{row.owner}</td>
                      <td className="py-2">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">No stalled projects in this range.</div>
          )}
        </ChartCard>
        
        {/* <ChartCard title="Blockers Themes" subtitle="Click a theme to see related tasks">
          {(dashboard?.series?.blockersThemes || []).length ? (
            <div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dashboard.series.blockersThemes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="theme" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    fill="#f97316"
                    radius={[4, 4, 0, 0]}
                    onClick={(payload) => {
                      const theme = payload?.theme || '';
                      setSelectedBlockerTheme((prev) => (prev === theme ? '' : theme));
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">No blockers detected in range.</div>
          )}
        </ChartCard> */}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* <ChartCard title="Stalled Projects" subtitle="XS/S/M projects exceeding age threshold">
          {(dashboard?.tables?.stalledProjects || []).length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2 pr-3">Project</th>
                    <th className="text-right py-2 pr-3">Age (days)</th>
                    <th className="text-left py-2 pr-3">Owner</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.tables.stalledProjects.slice(0, 10).map((row) => (
                    <tr key={row.taskId} className="border-b last:border-0">
                      <td className="py-2 pr-3 max-w-xs whitespace-normal break-words">{row.title}</td>
                      <td className="py-2 pr-3 text-right">{row.ageDays}</td>
                      <td className="py-2 pr-3">{row.owner}</td>
                      <td className="py-2">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">No stalled projects in this range.</div>
          )}
        </ChartCard> */}
      </section>

      {isCompanyViewer && (
        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ChartCard title="Company Heatmap" subtitle="Last 4 weeks by manager">
            <HeatmapGrid rows={dashboard?.series?.managerHeatmap || []} rowKey="managerName" endDate={filters.end} />
          </ChartCard>

          <ChartCard title="Hours by Client (Stacked by person)">
          {stackedCustomerData.data.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stackedCustomerData.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="customer" />
                <YAxis />
                <Tooltip />
                <Legend />
                {stackedCustomerData.people.map((person, index) => (
                  <Bar
                    key={person}
                    dataKey={person}
                    stackId="hours"
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                    onClick={(data) => {
                      const label = data?.customer || '';
                      const mappedClientId = label ? clientLabelToId.get(label) || '' : '';
                      setFilters((prev) => ({ ...prev, clientId: mappedClientId }));
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-500">No client distribution data.</div>
          )}
        </ChartCard>

          <ChartCard title="Completions & Active Backlog Trend">
            {(dashboard?.series?.completionsAndBacklogTrend || []).length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dashboard.series.completionsAndBacklogTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completions" stroke="#16a34a" strokeWidth={2} />
                  <Line type="monotone" dataKey="activeBacklog" stroke="#2563eb" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">No trend data in this range.</div>
            )}
          </ChartCard>

          <ChartCard title="Top Clients by Active Projects + Hours">
            {(dashboard?.series?.customerOverview || []).length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dashboard.series.customerOverview.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="customer" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="activeProjects" fill="#2563eb" />
                  <Bar dataKey="hours" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">No client overview in this range.</div>
            )}
          </ChartCard>

        </section>
      )}
    </DashboardLayout>
  );
};

export default TeamDashboard;
